import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import Select, { components } from "react-select";
import AsyncSelect from "react-select/async";
import MultiSelect from "./multi-select.jsx";
import CreatableSelect from "react-select/creatable";
import AsyncMultiSelect from "./async-multi-select.jsx";
import {clearRequiredFieldError, resetRequiredFieldError} from "../../utils/project-utils.js";
import {useRequiredFieldError} from "../../hooks/use-required-field-error.js";

const Option = (props) => {
    const Icon = props.data.icon;
    const showPath = !props.selectProps.hideOptionPath;
    const wrapValues = props.selectProps.wrapValues;

    return (
        <components.Option {...props}>
            <div className="flex items-center gap-2 min-w-0" title={props.data.path || props.data.label}>
                {Icon && <Icon size={props.data.iconSize} className="shrink-0" />}

                <div className="min-w-0">
                    <div className={`${wrapValues ? "whitespace-normal break-words" : "truncate"} text-[13px]`}>
                        {props.data.label}
                    </div>

                    {showPath && props.data.path && (
                        <div className={`${wrapValues ? "whitespace-normal break-words" : "truncate"} text-[11px] ${props.isSelected ? "text-white" : "text-gray-500"}`}>
                            {props.data.path}
                        </div>
                    )}
                </div>
            </div>
        </components.Option>
    );
};

const SelectComponent = (props) => {
    const selectRef = useRef(null);
    const containerRef = useRef(null);
    const {isDisabled} = props;
    const hasRequiredLabel = typeof props.label === "string" && props.label.trim().endsWith("*");
    const isRequired = !props.skipRequiredValidation && (props.required || hasRequiredLabel);
    const showRequiredIndicator = props.required || hasRequiredLabel;
    const classNameWrapper = typeof props.classNameWrapper === "string" ? props.classNameWrapper : "";
    const hasRequiredBackground = classNameWrapper.includes("bg-[#fdfdee]");
    const labelText = typeof props.label === "string" ? props.label.replace(/\s*\*\s*$/, "") : props.label;
    const [activeLabel, setActiveLabel] = useState(!props.isMulti ? !!props.value : !!props.value.length);
    const [hasSelectedOption, setHasSelectedOption] = useState(false);
    const hasSelectedValue = props.isMulti ? Boolean(props.value?.length) : props.value !== null && props.value !== undefined && props.value !== "";
    const hasSelection = props.value === undefined ? hasSelectedOption : hasSelectedValue;
    const requiredFieldInvalid = useRequiredFieldError(containerRef, isRequired);
    const previousIsRequired = useRef(isRequired);

    useEffect(() => {
        setActiveLabel(!props.isMulti ? !!props.value : !!props.value.length);
    }, [props.isMulti, props.value]);

    useEffect(() => {
        if (typeof props.reference === "function") {
            props.reference(selectRef.current);
            return;
        }

        if (props.reference) props.reference.current = selectRef.current;
    }, [props.reference]);

    useLayoutEffect(() => {
        const input = selectRef.current?.inputRef;

        if (typeof props.inputRef === "function") {
            props.inputRef(input);
        } else if (props.inputRef) {
            props.inputRef.current = input;
        }

        return () => {
            if (typeof props.inputRef === "function") {
                props.inputRef(null);
                return;
            }

            if (props.inputRef?.current === input) props.inputRef.current = null;
        };
    }, [props.inputRef, props.isAsync, props.isCreatable, props.isMulti]);

    useEffect(() => {
        if (isRequired && hasSelection) clearRequiredFieldError(containerRef.current);
    }, [hasSelection, isRequired]);

    useEffect(() => {
        const becameOptional = previousIsRequired.current && !isRequired;

        if (isDisabled || becameOptional) resetRequiredFieldError(containerRef.current);

        previousIsRequired.current = isRequired;
    }, [isDisabled, isRequired]);

    const onMenuOpen = () => setActiveLabel(true);
    const onMenuClose = () => {
        if (!props.value || (props.isMulti && !props.value.length)) {
            setActiveLabel(false);
        }
    };

    const hasRequiredError = !isDisabled && isRequired && !hasSelection && (props.invalid || requiredFieldInvalid);
    const hasError =
        hasRequiredError ||
        (!isDisabled && props.invalid && !hasSelection) ||
        props.className?.includes("border-red-500") ||
        props.labelClass?.includes("text-red-500");

    const status = props.status;
    const menuPortalZIndex = props.menuPortalZIndex ?? 2000;
    const openMenuOnFocus = props.openMenuOnFocus ?? true;

    const statusColors = {
        danger: { text: "#dc4c64" },
        warning: { text: "#FC6D00" },
    };
    const c = status ? statusColors[status] : null;

    const customStyles = {
        container: (base) => ({
            ...base,
            borderRadius: "0.375rem"
        }),
        control: (base, state) => ({
            ...base,
            height: "32px",
            minHeight: "32px",
            borderWidth: hasError ? "2px" : "1px",
            borderColor: hasError ? "#dc2626" : "#e5e7eb",
            boxShadow: hasError ? "none" : (state.isFocused ? "0 0 0 1px #60a5fa" : "none"),
            transition: "border-color 150ms ease-out, border-width 150ms ease-out, box-shadow 150ms ease-out",
            "&:hover": {
                borderColor: hasError ? "#dc2626" : "#9ca3af",
            },
            backgroundColor: props?.isDisabled ? "#e9ecef" : hasRequiredBackground ? "#fdfdee" : "#ffffff",
            borderRadius: "0.375rem",
            paddingRight: props.customIcon ? "25px" : base.paddingRight,
            cursor: "pointer"
        }),
        valueContainer: (base) => ({
            ...base,
            maxHeight: "65px",
            overflow: "auto",
            fontSize: "13px",
            color: "#676a6c"
        }),
        option: (base) => ({
            ...base,
            maxHeight: props.wrapValues ? "none" : "65px",
            fontSize: "13px",
        }),
        menuPortal: (def) => ({...def, zIndex: menuPortalZIndex}),
        singleValue: (def) => ({
            ...def,
            top: "-2px",
            position: "relative",
            zIndex: 100,
            color: c ? c.text : def.color,
        }),
        indicatorsContainer: (def) => ({...def, top: "-3px", position: "relative"}),
    };

    const handleLabelClick = () => {
        if (selectRef.current && !isDisabled) {
            selectRef.current.openMenu();
            selectRef.current.focus();
            setActiveLabel(true);
        }
    }

    const handleChange = (selectedOption) => {
        setHasSelectedOption(props.isMulti ? Boolean(selectedOption?.length) : Boolean(selectedOption));
        props.onChange?.(selectedOption);
        if (!props.isMulti) selectRef.current?.blur();
    };

    const options = props.options ?? props.defaultOptions ?? [];
    const value = ((typeof props.value === "string" || typeof props.value === "number") && props.value) ? options.find(opt => opt.value?.toString() === props.value.toString()) ?? props.value : props.value;

    return (
        <div
            ref={containerRef}
            className={"relative cursor-pointer " + classNameWrapper + " min-w-0"}
            data-required-field={isRequired || undefined}
            data-required-disabled={isDisabled || undefined}
            data-required-has-value={hasSelection ? "true" : "false"}
        >
            {
                props.isAsync && !props.isMulti &&
                <AsyncSelect
                    ref={selectRef}
                    {...props}
                    value={value}
                    placeholder={""}
                    styles={customStyles}
                    onMenuOpen={onMenuOpen}
                    onMenuClose={onMenuClose}
                    onChange={handleChange}
                    onInputChange={() => setActiveLabel(true)}
                    loadingMessage={() => "Carregando..."}
                    noOptionsMessage={() => "Nenhum item encontrado"}
                    isClearable={true}
                    openMenuOnFocus={openMenuOnFocus}
                    wrapValues={props.wrapValues}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    aria-invalid={hasError}
                />
            }

            {
                !props.isAsync && !props.isMulti && !props.isCreatable &&
                <Select
                    {...props}
                    ref={selectRef}
                    placeholder={""}
                    styles={customStyles}
                    components={{ Option }}
                    onMenuOpen={onMenuOpen}
                    onMenuClose={onMenuClose}
                    openMenuOnFocus={openMenuOnFocus}
                    onChange={handleChange}
                    loadingMessage={() => "Carregando..."}
                    noOptionsMessage={() => "Nenhum item encontrado"}
                    isClearable={props?.isClearable ?? true}
                    menuPlacement={props.menuPlacement || 'auto'}
                    value={value}
                    wrapValues={props.wrapValues}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    aria-invalid={hasError}
                />
            }

            {
                props.isMulti && !props.isAsync &&
                <MultiSelect
                    {...props}
                    invalid={hasError}
                    aria-invalid={hasError}
                    placeholder={""}
                    openMenuOnFocus={openMenuOnFocus}
                    onMenuOpen={onMenuOpen}
                    onMenuClose={onMenuClose}
                    onChange={handleChange}
                    loadingMessage={() => "Carregando..."}
                    place={() => "Carregando..."}
                    noOptionsMessage={() => "Nenhum item encontrado"}
                    selectRef={selectRef}
                    wrapValues={props.wrapValues}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    setActiveLabel={setActiveLabel}
                />
            }

            {
                props.isMulti && props.isAsync &&
                <AsyncMultiSelect
                    {...props}
                    invalid={hasError}
                    aria-invalid={hasError}
                    placeholder={""}
                    openMenuOnFocus={openMenuOnFocus}
                    onMenuOpen={onMenuOpen}
                    onMenuClose={onMenuClose}
                    onChange={handleChange}
                    loadingMessage={() => "Carregando..."}
                    place={() => "Carregando..."}
                    noOptionsMessage={() => "Nenhum item encontrado"}
                    selectRef={selectRef}
                    wrapValues={props.wrapValues}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                />
            }

            {
                props.isCreatable &&
                <CreatableSelect
                    {...props}
                    ref={selectRef}
                    value={value}
                    placeholder={""}
                    styles={customStyles}
                    onMenuOpen={onMenuOpen}
                    onMenuClose={onMenuClose}
                    onChange={handleChange}
                    openMenuOnFocus={openMenuOnFocus}
                    loadingMessage={() => "Carregando..."}
                    noOptionsMessage={() => "Nenhum item encontrado"}
                    isClearable={props?.isClearable ?? true}
                    createOptionPosition="first"
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    aria-invalid={hasError}
                />
            }

            {props.customIcon && (
                <div className="pointer-events-none absolute top-[10px] right-[12px]">
                    {props.customIcon}
                </div>
            )}

            {props.helperText && (
                <small
                    className="absolute -top-3.5 right-0"
                    style={c ? { color: c.text } : undefined}
                >
                    {props.helperText}
                </small>
            )}

            <label htmlFor={props.instanceId}
                   title={props.label}
                   onClick={handleLabelClick}
                   style={c ? { color: c.text } : undefined }
                   className={"truncate min-w-0 max-w-[calc(100%-50px)] absolute text-sm text-gray-500 duration-300 transform " +
                   `${activeLabel ? " -translate-y-4 scale-75 max-w-full" : "scale-100"} top-[5px] z-10 origin-[0] px-1 left-2.5 ` +
                   (isDisabled ? (activeLabel ? "bg-label-disabled" : "") : "bg-[linear-gradient(to_bottom,transparent_50%,white_50%)] "  + (props.labelClass || ''))}>
                {labelText}{showRequiredIndicator ? <span className="ml-0.5">*</span> : null}
            </label>
        </div>
    );
}

export default SelectComponent;